import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { PaymentsWalletsService } from "../services/paymentsWallets.service";
import type {
  RuntimeWallet,
  RuntimeWalletTransaction,
  WalletStatementFilters,
} from "../types/payments-wallets.types";
import { applyWalletBalance, useWalletBalanceSocket } from "./useWalletBalanceSocket";

export const STATEMENT_PAGE_SIZE = 25;
export const DEFAULT_STATEMENT_FILTERS: WalletStatementFilters = {
  operation_type: "all",
  direction: "all",
  from: "",
  to: "",
};

/** Session-scoped access latch — mirrors `useRuntimeWallets`. */
const deniedPrincipals = new Set<string>();

export interface UseWalletStatementResult {
  wallet: RuntimeWallet | null;
  transactions: RuntimeWalletTransaction[];
  total: number;
  page: number;
  setPage: (page: number) => void;
  filters: WalletStatementFilters;
  setFilters: (filters: WalletStatementFilters) => void;
  /** Transactions page load. */
  isLoading: boolean;
  /** Wallet header load (first fetch of the wallet). */
  isLoadingWallet: boolean;
  hasAccess: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Drives the wallet statement (movements) view off the runtime: the wallet header (`GET /:id`) plus its
 * paginated ledger legs (`GET /:id/transactions`) with server-side `operation_type`/`direction` filters. A
 * 401/403 latches for the session and surfaces via `hasAccess`.
 */
export const useWalletStatement = (walletId: string): UseWalletStatementResult => {
  const { user } = useAuth();
  const principalKey = user?.id ?? "anon";

  const [wallet, setWallet] = useState<RuntimeWallet | null>(null);
  const [transactions, setTransactions] = useState<RuntimeWalletTransaction[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFiltersState] = useState<WalletStatementFilters>(DEFAULT_STATEMENT_FILTERS);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingWallet, setIsLoadingWallet] = useState(true);
  const [hasAccess, setHasAccess] = useState(() => !deniedPrincipals.has(principalKey));
  const [error, setError] = useState<string | null>(null);
  const txSeq = useRef(0);

  // Live balance for the wallet on screen (the hero renders available / pending / blocked).
  useWalletBalanceSocket((payload) => {
    if (payload.id !== walletId) return;
    setWallet((current) => (current ? applyWalletBalance([current], payload)[0] : current));
  });

  const setFilters = useCallback((next: WalletStatementFilters) => {
    setFiltersState(next);
    setPage(1);
  }, []);

  const fetchWallet = useCallback(async () => {
    if (!walletId || deniedPrincipals.has(principalKey)) {
      setIsLoadingWallet(false);
      return;
    }
    setIsLoadingWallet(true);
    try {
      const res = await PaymentsWalletsService.getById(walletId);
      setWallet(res.data);
      setHasAccess(true);
    } catch (err: unknown) {
      if (HttpApiService.isAccessDeniedError(err)) {
        deniedPrincipals.add(principalKey);
        setHasAccess(false);
      } else {
        setError(HttpApiService.getErrorMessage(err) || null);
      }
    } finally {
      setIsLoadingWallet(false);
    }
  }, [walletId, principalKey]);

  const fetchTransactions = useCallback(async () => {
    if (!walletId || deniedPrincipals.has(principalKey)) {
      setIsLoading(false);
      return;
    }
    const seq = ++txSeq.current;
    setIsLoading(true);
    setError(null);
    try {
      const res = await PaymentsWalletsService.listTransactions(walletId, {
        operation_type: filters.operation_type === "all" ? undefined : filters.operation_type,
        direction: filters.direction === "all" ? undefined : filters.direction,
        // Inclusive day bounds in the USER's timezone (the picker returns local days; rows render local
        // times), converted to UTC ISO for the backend range over `posted_at`.
        from: filters.from ? new Date(`${filters.from}T00:00:00.000`).toISOString() : undefined,
        to: filters.to ? new Date(`${filters.to}T23:59:59.999`).toISOString() : undefined,
        limit: STATEMENT_PAGE_SIZE,
        skip: (page - 1) * STATEMENT_PAGE_SIZE,
      });
      if (seq !== txSeq.current) return;
      setTransactions(res.data.data);
      setTotal(res.data.total);
      setHasAccess(true);
    } catch (err: unknown) {
      if (seq !== txSeq.current) return;
      if (HttpApiService.isAccessDeniedError(err)) {
        deniedPrincipals.add(principalKey);
        setHasAccess(false);
      } else {
        setError(HttpApiService.getErrorMessage(err) || null);
        setTransactions([]);
        setTotal(0);
      }
    } finally {
      if (seq === txSeq.current) setIsLoading(false);
    }
  }, [walletId, principalKey, filters.operation_type, filters.direction, filters.from, filters.to, page]);

  useEffect(() => {
    void fetchWallet();
  }, [fetchWallet]);

  useEffect(() => {
    void fetchTransactions();
  }, [fetchTransactions]);

  const refresh = useCallback(async () => {
    await Promise.all([fetchWallet(), fetchTransactions()]);
  }, [fetchWallet, fetchTransactions]);

  return {
    wallet,
    transactions,
    total,
    page,
    setPage,
    filters,
    setFilters,
    isLoading,
    isLoadingWallet,
    hasAccess,
    error,
    refresh,
  };
};
